import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, Download, Loader2, Receipt, Calendar, DollarSign, Wallet, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyInvoices({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  // Sprint 3 (May 2026) — credit visibility
  const [creditsSummary, setCreditsSummary] = useState(null);
  const [expandedChild, setExpandedChild] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchCredits();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/academy/invoices/my-invoices`, { withCredentials: true });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/academy/parent/credits-summary`, { withCredentials: true });
      setCreditsSummary(response.data || null);
    } catch (error) {
      // Soft-fail: a parent without credits should NOT see an error toast.
      console.warn("Credits summary unavailable:", error);
    }
  };

  const downloadInvoice = async (invoice) => {
    setDownloading(invoice.reference_id);
    try {
      const response = await axios.get(
        `${API}/academy/invoices/${invoice.invoice_type}/${invoice.reference_id}/pdf`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { variant: "success", label: "Paid", className: "bg-emerald-500/20 text-emerald-400" },
      verified: { variant: "success", label: "Verified", className: "bg-emerald-500/20 text-emerald-400" },
      approved: { variant: "success", label: "Approved", className: "bg-emerald-500/20 text-emerald-400" },
      pending: { variant: "warning", label: "Pending", className: "bg-yellow-500/20 text-yellow-400" },
      pending_payment: { variant: "warning", label: "Awaiting Payment", className: "bg-yellow-500/20 text-yellow-400" },
      rejected: { variant: "destructive", label: "Rejected", className: "bg-red-500/20 text-red-400" },
      cancelled: { variant: "secondary", label: "Cancelled", className: "bg-slate-800/50 text-white" }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
  };

  const getInvoiceTypeIcon = (type) => {
    const icons = {
      enrollment: "📚",
      registration: "📝",
      adhoc: "⚡",
      outstanding: "💰"
    };
    return icons[type] || "📄";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-SG", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Receipt className="h-6 w-6 text-blue-400" />
              My Invoices
            </h1>
            <p className="text-xs text-slate-500 mt-1" data-testid="invoice-issuer-line">
              Invoiced by Football And More Limited · UEN 202436060M
            </p>
          </div>
        </div>

        {/* Sprint 3 (May 2026) — credits card. Only render when the parent has at least one child. */}
        {creditsSummary && creditsSummary.children && creditsSummary.children.length > 0 && (
          <Card className="mb-6 bg-emerald-500/5 border-emerald-500/30" data-testid="parent-credits-card">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                    My credit balance
                  </CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    Credits issued for cancellations, make-up sessions or goodwill. Auto-applies on your next invoice.
                  </CardDescription>
                </div>
                <div className="text-right">
                  {creditsSummary.total_session_balance > 0 && (
                    <div className="text-emerald-400 font-semibold text-base" data-testid="total-session-balance">
                      {creditsSummary.total_session_balance} session credit{creditsSummary.total_session_balance === 1 ? "" : "s"}
                    </div>
                  )}
                  {creditsSummary.total_monetary_balance > 0 && (
                    <div className="text-emerald-400 font-semibold text-base" data-testid="total-monetary-balance">
                      ${creditsSummary.total_monetary_balance.toFixed(2)} credit
                    </div>
                  )}
                  {(creditsSummary.total_session_balance + creditsSummary.total_monetary_balance) === 0 && (
                    <div className="text-slate-500 text-sm">No active credits</div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {creditsSummary.children.map((child) => {
                  const isExpanded = expandedChild === child.player_id;
                  const hasCredits = child.session_balance > 0 || child.monetary_balance > 0;
                  const hasHistory = (child.history || []).length > 0;
                  return (
                    <div
                      key={child.player_id}
                      className={`rounded-lg border transition-colors ${hasCredits ? "bg-emerald-500/5 border-emerald-500/30" : "bg-slate-800/40 border-slate-700/60"}`}
                      data-testid={`credit-child-${child.player_id}`}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedChild(isExpanded ? null : child.player_id)}
                        disabled={!hasHistory}
                        className="w-full flex items-center justify-between p-3 text-left disabled:cursor-default"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{child.player_name || "Child"}</div>
                          {child.membership_number && (
                            <div className="text-xs text-slate-500 font-mono">#{child.membership_number}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {child.session_balance > 0 && (
                            <span className="text-emerald-400 text-sm">
                              <span className="font-semibold">{child.session_balance}</span> session
                              {child.session_balance === 1 ? "" : "s"}
                              {child.session_value_estimate > 0 && (
                                <span className="text-emerald-400/60 text-xs ml-1">
                                  (~${child.session_value_estimate.toFixed(0)})
                                </span>
                              )}
                            </span>
                          )}
                          {child.monetary_balance > 0 && (
                            <span className="text-emerald-400 text-sm font-semibold">
                              ${child.monetary_balance.toFixed(2)}
                            </span>
                          )}
                          {!hasCredits && (
                            <span className="text-slate-500 text-xs">No credit</span>
                          )}
                          {hasHistory && (
                            isExpanded
                              ? <ChevronUp className="w-4 h-4 text-slate-400" />
                              : <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </button>

                      {isExpanded && hasHistory && (
                        <div className="border-t border-slate-700/60 px-3 py-2 space-y-1.5" data-testid={`credit-history-${child.player_id}`}>
                          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium pb-1">Activity</div>
                          {child.history.map((h) => {
                            const isGrant = h.type === "grant";
                            const isMonetary = h.credit_type === "monetary";
                            const amountLabel = isMonetary
                              ? `$${(h.amount || 0).toFixed(2)}`
                              : `${h.credits || 0} session${(h.credits || 0) === 1 ? "" : "s"}`;
                            return (
                              <div key={h.id} className="flex items-start justify-between gap-2 text-sm py-1">
                                <div className="flex-1 min-w-0">
                                  <div className={isGrant ? "text-emerald-400" : "text-blue-400"}>
                                    {isGrant ? "+ " : "− "}{amountLabel}
                                    <span className="text-slate-500 ml-2 text-xs">
                                      {h.created_at ? new Date(h.created_at).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : ""}
                                    </span>
                                  </div>
                                  {(h.reason_details || h.reason) && (
                                    <div className="text-xs text-slate-400 truncate" title={h.reason_details || h.reason}>
                                      {h.reason_details || h.reason}
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs text-slate-500 flex-shrink-0">{isGrant ? "Granted" : "Used"}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {creditsSummary.children.length > 0 && (creditsSummary.total_session_balance > 0 || creditsSummary.total_monetary_balance > 0) && (
                <div className="mt-3 text-xs text-emerald-300/80 italic" data-testid="credit-auto-apply-hint">
                  💡 These credits will automatically reduce the amount due on your next academy invoice.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mb-6 bg-blue-500/10 border-blue-500/30">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-400">
                <p className="font-medium mb-1">Download your invoices</p>
                <p>View and download PDF invoices for all your academy payments including registrations, enrollments, and ad-hoc sessions.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices List */}
        {invoices.length === 0 ? (
          <Card className="p-8 text-center">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Invoices Yet</h3>
            <p className="text-slate-400">Your invoices will appear here once you make payments.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <Card key={`${invoice.invoice_type}-${invoice.reference_id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">{getInvoiceTypeIcon(invoice.invoice_type)}</div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">{invoice.invoice_number}</h3>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="text-sm text-slate-400">{invoice.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(invoice.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            ${invoice.amount?.toFixed(2)}
                          </span>
                          <span className="capitalize px-2 py-0.5 bg-slate-800/50 rounded text-slate-400">
                            {invoice.invoice_type}
                          </span>
                        </div>
                        {/* Sprint 3 (May 2026) — credit auto-apply hint on unpaid invoices */}
                        {(() => {
                          const isUnpaid = ["pending", "pending_payment", "awaiting_payment"].includes((invoice.status || "").toLowerCase());
                          const hasCredit = creditsSummary && (
                            (creditsSummary.total_session_balance || 0) > 0 ||
                            (creditsSummary.total_monetary_balance || 0) > 0
                          );
                          if (!isUnpaid || !hasCredit) return null;
                          const sessions = creditsSummary.total_session_balance || 0;
                          const monetary = creditsSummary.total_monetary_balance || 0;
                          const parts = [];
                          if (sessions > 0) parts.push(`${sessions} session credit${sessions === 1 ? "" : "s"}`);
                          if (monetary > 0) parts.push(`$${monetary.toFixed(2)} credit`);
                          return (
                            <div
                              className="mt-2 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300 inline-block"
                              data-testid={`invoice-credit-hint-${invoice.reference_id}`}
                            >
                              💡 {parts.join(" + ")} will auto-apply on payment
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => downloadInvoice(invoice)}
                      disabled={downloading === invoice.reference_id}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      {downloading === invoice.reference_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary */}
        {invoices.length > 0 && (
          <Card className="mt-6 bg-[#0a0f1a]">
            <CardContent className="pt-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Total Invoices: {invoices.length}</span>
                <span className="font-semibold">
                  Total Amount: ${invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
